/**
 * Recently viewed products.
 *
 * Horizon already records viewed product ids in localStorage ("viewedProducts",
 * newest first) on every product page. This reads that list, leaves out the
 * product being viewed, and asks the ddd-recently-viewed section to render
 * those products through the search results page. It only fetches when the
 * row is about to scroll into view, and stays hidden when there is nothing
 * to show.
 */
class DddRecentlyViewed extends HTMLElement {
  connectedCallback() {
    const ids = this.#ids();
    if (ids.length === 0) return;

    if (!('IntersectionObserver' in window)) {
      this.#load(ids);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        this.#load(ids);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    observer.observe(this);
  }

  /** @returns {string[]} */
  #ids() {
    let ids = [];
    try {
      ids = JSON.parse(localStorage.getItem('viewedProducts') || '[]');
    } catch (error) {
      return [];
    }
    if (!Array.isArray(ids)) return [];

    const exclude = this.dataset.exclude;
    const limit = Number(this.dataset.limit) || 4;
    return ids.filter((id) => String(id) !== exclude).slice(0, limit);
  }

  /** @param {string[]} ids */
  async #load(ids) {
    const url = new URL(this.dataset.searchUrl || '/search', window.location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('section_id', 'ddd-recently-viewed');

    try {
      const response = await fetch(url);
      if (!response.ok) return;

      const html = await response.text();
      const incoming = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ddd-rv-list]');
      const target = this.querySelector('[data-ddd-rv-list]');
      if (!incoming || !target || incoming.children.length === 0) return;

      // Search does not keep the order of the ids, so put the newest first again.
      const items = Array.from(incoming.children).sort(
        (a, b) => ids.indexOf(a.getAttribute('data-product-id')) - ids.indexOf(b.getAttribute('data-product-id'))
      );
      target.replaceChildren(...items);
      this.hidden = false;
    } catch (error) {
      // A failed fetch leaves the row hidden; nothing else depends on it.
    }
  }
}

if (!customElements.get('ddd-recently-viewed')) {
  customElements.define('ddd-recently-viewed', DddRecentlyViewed);
}
